import { clsx } from 'clsx';
import { Fragment, FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { TableColumn, TableRow, TableRowSpanningBoxProps } from './interfaces/table';
import {
  getTableBodyCellLayoutProps,
  getTableCellId,
  getTableCellLayoutProps
} from './utils/tableUtils';
import { Tooltip } from '../tooltip/Tooltip';
import styles from './Table.module.scss';
import { getDataIdGenerator } from '../shared/utils/dataId';
import { ListConductorSort, SortDirection } from '../list-conductor/sort';
import { SortButton } from '../sort/SortButton';
import { Checkbox } from '../checkbox/Checkbox';
import { BulkActionHeader } from './BulkActionTableHeader';

export interface TableProps<
  T extends object = object,
  G extends object = object,
  B extends object = object
> {
  dataId?: string;
  columns: TableColumn<T, G>[];
  rows: TableRow<T>[];
  globalContext?: G;
  classes?: {
    containerClass?: string;
    headerClass?: string;
    rowContainerClass?: string;
    rowClass?: string;
  };
  fillSpace?: boolean;
  renderHeader?: boolean;
  withoutShadow?: boolean;
  compactHeader?: boolean;
  withoutHeaderDivider?: boolean;
  bulkSelection?: boolean;
  sortState?: ListConductorSort;
  disableClick?: boolean;
  onSort?: (sortKey: string, order: SortDirection) => void;
  forceFullWidth?: boolean;
  RowSpanningBox?: FunctionComponent<TableRowSpanningBoxProps<T, G>>;
  onItemSelectionChange?: (row: T, selected: boolean) => void;
  onAllInPageSelectionChange?: (selected: boolean) => void;
  onAllItemsSelectionChange?: (selected: boolean) => void;
  totalCount?: number;
  selectedItems?: Set<number | string>;
  BulkActionsComponent?: FunctionComponent<B>;
  bulkActionsComponentProps?: B;
  entity?: string;
}

const CHECKBOX_SIZE = 38;
const CELL_MARGIN = 20;

export function Table<T extends object = object, G extends object = object>({
  dataId = '',
  columns,
  rows,
  globalContext,
  classes = {},
  fillSpace = true,
  renderHeader = true,
  withoutShadow = false,
  compactHeader = false,
  withoutHeaderDivider = false,
  forceFullWidth = false,
  sortState,
  onSort,
  RowSpanningBox,
  bulkSelection = false,
  onItemSelectionChange = () => {},
  disableClick = false,
  selectedItems = new Set([]),
  totalCount = 0,
  onAllInPageSelectionChange = () => {},
  onAllItemsSelectionChange = () => {},
  BulkActionsComponent,
  bulkActionsComponentProps,
  entity = ''
}: TableProps<T, G>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [isOverflowingContainer, setIsOverflowingContainer] = useState(false);
  useEffect(() => {
    if (tableRef.current) {
      setIsOverflowingContainer(tableRef.current.scrollWidth > tableRef.current.clientWidth);
    }
  }, [columns]);

  const flexibleCellsCount = useMemo(() => {
    // 1 column as last column should be aligned right
    return columns.filter(column => column.isFlexible).length ?? 1;
  }, [columns]);

  const totalWidthExcludingFlexibleColumn = useMemo(() => {
    return columns.reduce(
      (acc, column) => {
        if (column.isFlexible) {
          return acc;
        }

        return acc + column.width + CELL_MARGIN;
      },
      bulkSelection ? CHECKBOX_SIZE : 0
    );
  }, [columns]);

  const tableHeaderCellsLayoutProps = useMemo(() => {
    const actionsColumnsExist = columns.filter(column => column.field === 'actions');

    if (actionsColumnsExist.length) {
      // eslint-disable-next-line no-param-reassign
      columns[columns.length - 2] = {
        ...columns[columns.length - 2],
        justifyEnd:
          typeof columns[columns.length - 2].justifyEnd === 'undefined'
            ? true
            : columns[columns.length - 2].justifyEnd
      };
    } else {
      // eslint-disable-next-line no-param-reassign
      columns[columns.length - 1] = {
        ...columns[columns.length - 1],
        justifyEnd:
          typeof columns[columns.length - 1].justifyEnd === 'undefined'
            ? true
            : columns[columns.length - 1].justifyEnd
      };
    }

    return columns.map(column => {
      return getTableCellLayoutProps({
        column,
        isOverflowingContainer,
        consumedWidth: totalWidthExcludingFlexibleColumn,
        flexibleCellsCount,
        fillSpace
      });
    });
  }, [
    columns,
    isOverflowingContainer,
    totalWidthExcludingFlexibleColumn,
    flexibleCellsCount,
    fillSpace
  ]);

  const tableHeaderDataIGenerator = getDataIdGenerator(dataId, 'table-header');
  const tableRowDataIdGenerator = getDataIdGenerator(dataId, 'table-row');
  const tableBodyDataIdGenerator = getDataIdGenerator(dataId, 'table-body');

  return (
    <table
      data-id={dataId}
      ref={tableRef}
      className={clsx(
        styles.tableContainer,
        withoutShadow && styles.withoutShadow,
        classes?.containerClass
      )}
    >
      {renderHeader ? (
        <thead>
          {bulkSelection ? (
            <tr
              className={clsx(styles.tableHeader, styles.tableHeaderWithBorder)}
              data-id={tableHeaderDataIGenerator('')}
            >
              <BulkActionHeader
                dataIdGenerator={tableHeaderDataIGenerator}
                selectedItems={selectedItems}
                totalCount={totalCount}
                onAllItemsSelectionChange={onAllItemsSelectionChange}
                BulkActionsComponent={BulkActionsComponent}
                bulkActionsComponentProps={bulkActionsComponentProps}
                entity={entity}
              />
            </tr>
          ) : null}
          <tr
            className={clsx(
              styles.tableHeader,
              !withoutHeaderDivider && styles.tableHeaderWithBorder,
              compactHeader && styles.tableHeaderCompact,
              classes?.headerClass
            )}
            data-id={tableHeaderDataIGenerator('')}
          >
            {bulkSelection ? (
              <th>
                <Checkbox
                  dataId={tableHeaderDataIGenerator('select-all')}
                  className='mr-5'
                  checked={rows.every(row => selectedItems.has(row.id))}
                  indeterminate={
                    !rows.every(row => selectedItems.has(row.id)) &&
                    rows.some(row => selectedItems.has(row.id))
                  }
                  onChange={(_, checked) => {
                    onAllInPageSelectionChange(checked);
                  }}
                />
              </th>
            ) : null}
            {columns.map((column, columnIndex) => (
              <th
                key={column.id}
                data-id={tableHeaderDataIGenerator(column.id.toString())}
                className={clsx(
                  styles.tableHeaderCell,
                  column.headerClassName,
                  column.justifyEnd && 'justify-end',
                  column.withoutBorder && styles.tableHeaderCellWithoutBorder
                )}
                style={tableHeaderCellsLayoutProps[columnIndex]}
              >
                <div
                  style={{
                    order: column.justifyEnd ? '2' : '1'
                  }}
                >
                  {column.renderHeaderCell ? (
                    <div className='text-ellipsis'>
                      {column.renderHeaderCell({ column, globalContext })}
                    </div>
                  ) : (
                    <span
                      className='center-flex-row text-ellipsis'
                      data-id={tableHeaderDataIGenerator(`header-name-${column.id.toString()}`)}
                    >
                      <Tooltip
                        dataId={tableHeaderDataIGenerator(
                          `header-name-${column.id.toString()}-tooltip`
                        )}
                        title={column.description}
                        disabled={!column?.description}
                      >
                        <p>{column.headerName}</p>
                      </Tooltip>
                    </span>
                  )}
                </div>

                {column.canSort ? (
                  <div
                    className='position-relative'
                    style={{
                      order: column.justifyEnd ? '1' : '2',
                      left: column.justifyEnd ? '-16px' : '16px'
                    }}
                  >
                    <SortButton
                      className={styles.sortButton}
                      unSortedStateClassname={styles.showOnTableColumnHover}
                      sortKey={column.sortKey as string}
                      sortState={sortState ?? {}}
                      onSort={onSort as (sortKey: string, order: SortDirection | undefined) => void}
                    />
                  </div>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
      ) : null}

      <tbody
        data-id={tableBodyDataIdGenerator('')}
        className={clsx(
          styles.tableRowContainer,
          forceFullWidth && styles.forceFullWidthBody,
          classes?.rowContainerClass
        )}
      >
        {rows.map((row, rowIndex) => {
          const cellsRenderState = columns.map(column => {
            const cell = { row, column, globalContext };

            return !column.shouldRenderCell || column.shouldRenderCell(cell);
          });

          return (
            <Fragment key={row.id}>
              <tr
                data-id={tableRowDataIdGenerator(`row-${row.id}`)}
                role={disableClick || !row.role ? 'presentation' : row.role}
                aria-describedby='table row'
                tabIndex={row.tabIndex || -1}
                onClick={() => {
                  if (disableClick || row.disabled) {
                    return;
                  }

                  if (row.onClick) {
                    row.onClick(row.data);
                  }
                }}
                className={clsx(
                  styles.tableRow,
                  !!row.disabled && styles.tableRowDisabled,
                  classes?.rowClass,
                  row.onClick && !disableClick && styles.clickableRow
                )}
              >
                {bulkSelection ? (
                  <th>
                    <Checkbox
                      dataId={tableRowDataIdGenerator(`row-${row.id}`)}
                      className='mr-5'
                      checked={selectedItems.has(row.id)}
                      onChange={(_, checked) => {
                        onItemSelectionChange(row.data, checked);
                      }}
                    />
                  </th>
                ) : null}
                {columns.map((column, columnIndex) => {
                  const cell = { row, column, globalContext };

                  const isCellRendered = cellsRenderState[columnIndex];

                  const tableCellLayoutProps = getTableBodyCellLayoutProps({
                    tableHeaderCellsLayoutProps,
                    isCellRendered,
                    columnIndex,
                    columns,
                    cell
                  });

                  let cellValue =
                    column.field in row.data
                      ? (row.data[column.field as keyof typeof row.data] as string | number)
                      : '-';

                  if (column.valueGetter) {
                    cellValue = column.valueGetter(cell);
                  }

                  const formattedValue = column.valueFormatter
                    ? column.valueFormatter(cell)
                    : cellValue;

                  return isCellRendered ? (
                    <td
                      key={getTableCellId(cell)}
                      data-id={tableBodyDataIdGenerator(`row-${getTableCellId(cell)}`)}
                      style={tableCellLayoutProps}
                      className={clsx(
                        column.cellClassName,
                        column.wrapOverflowingCellText && 'text-ellipsis',
                        { 'justify-end': column.justifyEnd }
                      )}
                    >
                      {column.renderCell ? (
                        column.renderCell(cell)
                      ) : (
                        <Tooltip
                          dataId=''
                          title={column.wrapOverflowingCellText ? formattedValue : null}
                          enableOnTextOverflow
                        >
                          <span
                            className={clsx(
                              column.wrapOverflowingCellText && 'd-block text-ellipsis'
                            )}
                          >
                            {formattedValue}
                          </span>
                        </Tooltip>
                      )}
                    </td>
                  ) : null;
                })}
              </tr>

              {RowSpanningBox ? (
                <tr className={styles.rowSpanningBoxRow}>
                  <td colSpan={columns.length}>
                    <RowSpanningBox
                      row={row}
                      globalContext={globalContext}
                      rowIndex={rowIndex}
                      totalRows={rows.length}
                    />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
